import {
    type ComponentProps,
    type ReactNode,
    type RefObject,
    useEffect,
    useState,
} from "react";
import classNames from "classnames";
import {
    TASK_CARD_FIELD_LABELS,
    type TaskCardEditField,
} from "./taskCardEdits";
import styles from "./TaskBoardChips.module.scss";

const FIELD_ATTRIBUTE = "data-task-field";
const CONTROL_ATTRIBUTE = "data-task-control";
/** Set by a picker on its trigger while the picker is open. */
export const OPEN_ATTRIBUTE = "data-popup-open";

/** A field a card chip shows: an editable field, or the title that renames in place. */
export type TaskChipField = TaskCardEditField | "title";

/** The card controls that aren't a field's picker: the kebab menu and Add field. */
const TASK_CARD_ACTION_CONTROLS = { actions: true, addField: true };

/** A card control that mounts once its card is armed: a field's picker, the kebab menu or Add field. */
export type TaskCardControl =
    | TaskCardEditField
    | keyof typeof TASK_CARD_ACTION_CONTROLS;

const isTaskCardEditField = (
    value: string | null | undefined,
): value is TaskCardEditField =>
    typeof value === "string" && Object.hasOwn(TASK_CARD_FIELD_LABELS, value);

const isTaskChipField = (
    value: string | null | undefined,
): value is TaskChipField => value === "title" || isTaskCardEditField(value);

const isTaskCardControl = (
    value: string | null | undefined,
): value is TaskCardControl =>
    typeof value === "string" &&
    (Object.hasOwn(TASK_CARD_ACTION_CONTROLS, value) ||
        isTaskCardEditField(value));

/**
 * Marks the trigger of `control`, which stands in for the control until its
 * card is armed (`useTaskCardArming`), so a click on it before then still
 * opens the control.
 */
export const taskControlProps = (control: TaskCardControl) => ({
    [CONTROL_ATTRIBUTE]: control,
});

/**
 * Marks the element showing `field` on a card, so a shortcut can find it.
 * An editable field's chip is also its control's trigger; the title isn't.
 */
export const taskFieldProps = (field: TaskChipField | undefined) => {
    const control: TaskCardControl | undefined =
        field === "title" ? undefined : field;
    return { [FIELD_ATTRIBUTE]: field, [CONTROL_ATTRIBUTE]: control };
};

/**
 * How a chip button shows hover and an open picker: `chip` lights the border
 * of the outline tag inside it (a `BoardCardTag`, the status pill, the chat
 * count), `bare` gives content without a border of its own (entities) an
 * input's border that hangs outside it, so the content doesn't move.
 */
export type TaskChipLook = "chip" | "bare";

type TaskChipButtonProps = ComponentProps<"button"> & {
    look?: TaskChipLook;
    /** The field the chip shows, so a shortcut can find it on the card. */
    field?: TaskChipField;
    /** Sent by `Picker.Select` to its trigger; a chip has no trailing icon. */
    rightIcon?: ReactNode;
};

/** The hover classes of a card control, for chip buttons and the Add field chip. */
export const getTaskChipClassName = (look: TaskChipLook) =>
    classNames(styles.chipButton, {
        [styles.chip]: look === "chip",
        [styles.bare]: look === "bare",
    });

/**
 * A card chip as a control trigger: a chromeless button around the chip with
 * the card's hover. Pointer only (`tabIndex={-1}`): the card stays the
 * board's one Tab stop, and the board ignores drags and clicks from it.
 */
export const TaskChipButton = ({
    look = "chip",
    field,
    rightIcon,
    className,
    children,
    ...buttonProps
}: Readonly<TaskChipButtonProps>) => (
    <button
        {...buttonProps}
        {...taskFieldProps(field)}
        type="button"
        tabIndex={-1}
        className={classNames(getTaskChipClassName(look), className)}
    >
        {children}
    </button>
);

/** The chip showing `field` on a card, if the card shows it. */
export const findTaskFieldChip = (card: HTMLElement, field: TaskChipField) =>
    card.querySelector<HTMLElement>(`[${FIELD_ATTRIBUTE}="${field}"]`);

/** The trigger of `control` on a card, if the card shows it. */
export const findTaskControl = (card: HTMLElement, control: TaskCardControl) =>
    card.querySelector<HTMLElement>(`[${CONTROL_ATTRIBUTE}="${control}"]`);

/** The control whose trigger holds `element`, or null outside one. */
export const getTaskControlOf = (element: Element): TaskCardControl | null => {
    const control = element
        .closest(`[${CONTROL_ATTRIBUTE}]`)
        ?.getAttribute(CONTROL_ATTRIBUTE);
    return isTaskCardControl(control) ? control : null;
};

/**
 * The field whose chip in the card has its picker open, or null, kept in
 * step as pickers open and close (their trigger's `data-popup-open`).
 */
export const useOpenTaskFieldChip = (
    cardRef: RefObject<HTMLElement | null>,
) => {
    const [openField, setOpenField] = useState<TaskChipField | null>(null);
    useEffect(() => {
        const card = cardRef.current;
        if (!card) {
            return undefined;
        }
        const observer = new MutationObserver(() => {
            const field = card
                .querySelector(`[${FIELD_ATTRIBUTE}][${OPEN_ATTRIBUTE}]`)
                ?.getAttribute(FIELD_ATTRIBUTE);
            setOpenField(isTaskChipField(field) ? field : null);
        });
        observer.observe(card, {
            subtree: true,
            attributeFilter: [OPEN_ATTRIBUTE],
        });
        return () => observer.disconnect();
    }, [cardRef]);
    return openField;
};
